import {
  Component,
  effect,
  ElementRef,
  Injector,
  input,
  OnInit,
  runInInjectionContext,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogService } from '@wheere-front/shared-util';

@Component({
  selector: 'lib-read-file',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="terminal-container" #terminalContainer (scroll)="onScroll()">
      <div class="terminal-line" *ngFor="let log of formattedLogs()" [innerHTML]="log"></div>
      <div *ngIf="fileStatus() === 'in_progress'" class="loader-spinner"></div>
      <div class="blinking-cursor">|</div>
    </div>
  `,
  styles: [
    `
      .loader-spinner {
        width: 32px;
        height: 32px;
        border: 4px solid #fff;
        border-top: 2px solid transparent;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto;
      }

      /* Terminal Container and Log Styles */
      .terminal-container {
        background-color: #1e1e1e;
        color: #ffffff;
        font-family: Consolas, 'Courier New', Courier, monospace;
        font-size: 14px;
        line-height: 1.4em;
        padding: 10px;
        border-radius: 5px;
        overflow: auto;
        height: 80vh;
        white-space: pre-wrap;
        position: relative;
      }

      .terminal-line {
        margin-bottom: 2px;
      }

      .blinking-cursor {
        display: inline-block;
        color: #ffffff;
        animation: blink 500ms infinite alternate;
      }

      @keyframes blink {
        from {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }

      /* Formatting for timestamps and brackets */
      .timestamp {
        color: #999999;
      }

      .brackets {
        color: #aaaaaa;
      }

      /* Filter Button Container */
      .filter-buttons {
        flex-grow: 1;
      }

      /* Loading Indicator */
      .loading-indicator {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: bold;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ReadFileComponent implements OnInit {
  deviceId = input.required<string>();
  fileContent = input.required<string[]>();
  fileStatus = input<string>('idle');
  formattedLogs = signal<SafeHtml[]>([]);
  replace = input<boolean>(false);
  rawFormattedLogs: string[] = [];

  @ViewChild('terminalContainer', { static: true })
  terminalContainer!: ElementRef;

  isAtBottom = true;

  constructor(
    private sanitizer: DomSanitizer,
    private injector: Injector,
    private logService: LogService
  ) {}

  ngOnInit() {
    runInInjectionContext(this.injector, () => {
      effect(
        () => {
          const rawLogs = this.fileContent() || [];
          if (this.replace()) {
            this.rawFormattedLogs = rawLogs.map(log => this.logService.formatLogLineRaw(log));
            this.formattedLogs.set(this.rawFormattedLogs.map(line => this.sanitizer.bypassSecurityTrustHtml(line)));
          } else {
            this.rawFormattedLogs = rawLogs;
            this.formattedLogs.set(this.rawFormattedLogs.map(value => JSON.stringify(value)));
          }

          if (this.isAtBottom) {
            setTimeout(() => this.scrollToBottom(), 0);
          }
        },
        { allowSignalWrites: true }
      );
    });
  }

  onScroll() {
    const container = this.terminalContainer.nativeElement;
    const threshold = 10;
    this.isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }

  scrollToBottom() {
    const container = this.terminalContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
    this.isAtBottom = true;
  }
}
